import type { DraftStyle } from "../components/boardTypes";
import type { DraftSyncRequest } from "../utils/draftSyncProviders";
import type { TeamOverridesState } from "../../shared/components/TeamsBoard";
import type { Player } from "../../shared/models/Player";
import type { RankingsListKey, TiersByPos } from "../../shared/utils/xlsxRankings";
import type { ImportedCompareList } from "../features/shared/draftCompanionHelpers";

const STORAGE_KEY = "fantasy-board:mobile-draft-companion:v1";

type MobileDraftSavedState = {
  version: 1;
  savedAt: string;
  rankingIdsByList?: Partial<Record<RankingsListKey, string[]>>;
  tiersByPosByList?: Partial<Record<RankingsListKey, TiersByPos>>;
  expertImport?: ImportedCompareList | null;
  expertPlayers?: Player[];
  extraPlayers?: Player[];
  planner?: {
    favoriteIds?: string[];
    plannerRoundById?: Record<string, number>;
    plannerOrderedIds?: string[];
  };
  draft?: {
    teams?: number;
    rounds?: number;
    draftStyle?: DraftStyle;
    teamNames?: string[];
    rosterStarterConfig?: Record<string, number>;
    rosterTeamOverrides?: TeamOverridesState;
    draftSlots?: Array<string | null>;
    draftSlotTeamIndexes?: Array<number | null>;
    draftedIds?: string[];
    draftedOrder?: string[];
    lastDraftSyncRequest?: DraftSyncRequest | null;
  };
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function nullableStringArray(value: unknown): Array<string | null> {
  return Array.isArray(value) ? value.map((item) => (typeof item === "string" ? item : null)) : [];
}

function nullableNumberArray(value: unknown): Array<number | null> {
  return Array.isArray(value)
    ? value.map((item) => (typeof item === "number" && Number.isFinite(item) ? item : null))
    : [];
}

function playerArray(value: unknown): Player[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is Player => isRecord(item) && typeof item.id === "string" && typeof item.name === "string");
}

function roundMap(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  const next: Record<string, number> = {};
  Object.entries(value).forEach(([id, round]) => {
    if (typeof round === "number" && Number.isFinite(round)) {
      next[id] = Math.max(1, Math.min(40, Math.round(round)));
    }
  });
  return next;
}

function tiersByPos(value: unknown): TiersByPos | undefined {
  if (!isRecord(value)) return undefined;
  return {
    QB: stringArray(value.QB),
    RB: stringArray(value.RB),
    WR: stringArray(value.WR),
    TE: stringArray(value.TE),
    K: stringArray(value.K),
    DST: stringArray(value.DST),
  };
}

function rankingsLists(value: unknown): Partial<Record<RankingsListKey, string[]>> {
  if (!isRecord(value)) return {};
  return {
    Rankings: stringArray(value.Rankings),
    Experts: stringArray(value.Experts),
  };
}

function tiersLists(value: unknown): Partial<Record<RankingsListKey, TiersByPos>> {
  if (!isRecord(value)) return {};
  return {
    Rankings: tiersByPos(value.Rankings),
    Experts: tiersByPos(value.Experts),
  };
}

export function loadMobileDraftSavedState(): MobileDraftSavedState | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isRecord(parsed) || parsed.version !== 1) return null;

    const rankingIdsByList = rankingsLists(parsed.rankingIdsByList);
    const tiersByPosByList = tiersLists(parsed.tiersByPosByList);
    const planner = isRecord(parsed.planner) ? parsed.planner : {};
    const draft = isRecord(parsed.draft) ? parsed.draft : {};

    return {
      version: 1,
      savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : "",
      rankingIdsByList,
      tiersByPosByList,
      expertImport: isRecord(parsed.expertImport) ? (parsed.expertImport as ImportedCompareList) : null,
      expertPlayers: playerArray(parsed.expertPlayers),
      extraPlayers: playerArray(parsed.extraPlayers),
      planner: {
        favoriteIds: stringArray(planner.favoriteIds),
        plannerRoundById: roundMap(planner.plannerRoundById),
        plannerOrderedIds: stringArray(planner.plannerOrderedIds),
      },
      draft: {
        teams: typeof draft.teams === "number" && Number.isFinite(draft.teams) ? Math.max(1, Math.round(draft.teams)) : undefined,
        rounds: typeof draft.rounds === "number" && Number.isFinite(draft.rounds) ? Math.max(1, Math.round(draft.rounds)) : undefined,
        draftStyle: typeof draft.draftStyle === "string" ? (draft.draftStyle as DraftStyle) : undefined,
        teamNames: stringArray(draft.teamNames),
        rosterStarterConfig: isRecord(draft.rosterStarterConfig) ? (draft.rosterStarterConfig as Record<string, number>) : undefined,
        rosterTeamOverrides: isRecord(draft.rosterTeamOverrides) ? (draft.rosterTeamOverrides as TeamOverridesState) : undefined,
        draftSlots: nullableStringArray(draft.draftSlots),
        draftSlotTeamIndexes: nullableNumberArray(draft.draftSlotTeamIndexes),
        draftedIds: stringArray(draft.draftedIds),
        draftedOrder: stringArray(draft.draftedOrder),
        lastDraftSyncRequest: isRecord(draft.lastDraftSyncRequest) ? (draft.lastDraftSyncRequest as DraftSyncRequest) : null,
      },
    };
  } catch {
    return null;
  }
}

export function saveMobileDraftSavedState(state: Omit<MobileDraftSavedState, "version" | "savedAt">) {
  if (typeof window === "undefined") return;

  const payload: MobileDraftSavedState = {
    ...state,
    version: 1,
    savedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage may be unavailable in private mode or full on-device storage.
  }
}
